import { CatmullRomCurve3, CurveType, Matrix4, Vector2, Vector3 } from 'three';

import { OBJECT_EDIT_YELLOW } from '@/config/color';
import { FatLine, LineGeometry, LineMaterial } from '@/lib/three';
import { ILineOptions, ILineUserData, WidgetObject } from '@/types';

import { Line3d, LineUpdateOptions } from './Line3d';

export class Spline3d {
  // 曲线类型
  static CURVE_TYPE: CurveType = 'centripetal';

  // 每两个关键点之间的插值数量，数量越大曲线拟合越平滑
  static INTERPOLATION_COUNT = 20;

  // TODO(xiudan): 增量的逻辑
  /**
   * 创建 3D 曲线
   * @param data [x1, y1, z1, x2, y2, z2...] three.js 坐标系
   * @param options 配置项
   * @returns
   */
  static create(data: WidgetObject['data'], options: ILineOptions): FatLine {
    // 此处不调用 Line3d 逻辑，因为不用处理增量的逻辑
    const { color, dashConfig, canvasSize = [1000, 1000] } = options;
    const geometry = new LineGeometry();
    const matConfig = {
      color,
      linewidth: 2,
      resolution: new Vector2(...canvasSize),
      alphaToCoverage: true,
      dashed: false,
      dashSize: 1,
      gapSize: 1,
      depthTest: false,
    };

    if (dashConfig) {
      matConfig.dashed = true;
      matConfig.dashSize = dashConfig[0];
      matConfig.gapSize = dashConfig[1];
    }

    const line = new FatLine(geometry, new LineMaterial(matConfig));
    line.name = line.uuid;
    const keyVectors = [];
    for (let i = 0; i < data.length; i += 3) {
      keyVectors.push(new Vector3(data[i], data[i + 1], data[i + 2]));
    }
    line.userData = {
      type: 'spline3d',
      keyVectors: keyVectors,
      keyPositions: data,
      ...options,
    } satisfies ILineUserData;

    if ((line.userData as ILineUserData).withDot) {
      const dots = Line3d.createPoints([], color, line.layers.mask);
      line.add(dots);
    }

    Spline3d.updatePoints(line, color);
    Spline3d.updateLine(line);

    return line;
  }

  /**
   * 全量更新 3D 曲线
   * @param target 目标物
   * @param data 待更新数据 [x1, y1, z1, x2, y2, z2...] three.js 坐标系
   * @param options
   */
  static update(target: FatLine, data: WidgetObject['data']) {
    Spline3d.partialUpdate(target, data, { startIndex: 0, mode: 'replace' });
  }

  /**
   * 更新 3D 曲线
   * @param target 目标物
   * @param data 待更新数据 [x1, y1, z1, x2, y2, z2...] three.js 坐标系
   * @param options
   */
  static partialUpdate(target: FatLine, data: WidgetObject['data'], options?: LineUpdateOptions) {
    const positions = Spline3d.getVertexFromObject3D(target);
    const { start, end, updateData, pointCount } = Line3d.getUpdateVertexs(positions, data, options);

    for (let i = start; i < end; i++) {
      target.userData.keyPositions[i] = updateData[i - start];
      if (i % 3 === 0) {
        target.userData.keyVectors[i / 3] = new Vector3(
          updateData[i - start],
          updateData[i - start + 1],
          updateData[i - start + 2]
        );
      }
    }

    target.userData.keyPositions = target.userData.keyPositions.slice(0, pointCount * 3);
    target.userData.keyVectors = target.userData.keyVectors.slice(0, pointCount);

    Spline3d.updatePoints(target, OBJECT_EDIT_YELLOW);
    Spline3d.updateLine(target);
  }

  /**
   * 根据 widget 生成曲线
   * @param name
   * @param widget
   * @param matrix
   * @param options
   */
  static createFromWidget(name: string, widget: WidgetObject, matrix: Matrix4, options: ILineOptions): FatLine {
    const data = Line3d.pointsApplyMatrix4(widget.data, matrix);
    const line = Spline3d.create(data, options);
    line.name = name;
    return line;
  }

  static updateFromWidget(target: FatLine, widget: WidgetObject, matrix: Matrix4) {
    Spline3d.update(target, Line3d.pointsApplyMatrix4(widget.data, matrix));
  }

  /**
   * 导出Widget格式数据
   * @param target
   * @param matrix 从 threejs 到文件坐标系
   */
  static transOutWidget(target: FatLine, matrix: Matrix4): WidgetObject {
    const data = Line3d.pointsApplyMatrix4(Spline3d.getVertexFromObject3D(target), matrix);
    return {
      name: 'line3d',
      data,
      line_type: 'crspline',
    };
  }

  /**
   * 全量更新曲线
   * @param target
   */
  static updateLine(target: FatLine) {
    const keyPoints = target.userData.keyVectors;
    if (keyPoints.length > 1) {
      const curve = new CatmullRomCurve3(keyPoints);
      curve.curveType = Spline3d.CURVE_TYPE;
      const points = curve.getPoints(Spline3d.INTERPOLATION_COUNT * (keyPoints.length - 1));
      // 设置线条新的渲染长度
      target.geometry.instanceCount = points.length - 1;
      target.geometry.setPositions(Spline3d.convertVectorsToArray(points));
    } else {
      target.geometry.instanceCount = 0;
      target.geometry.setPositions(Spline3d.convertVectorsToArray(keyPoints));
    }

    if (target.userData.dashConfig) {
      target.computeLineDistances();
    }
  }

  static updatePoints(target: FatLine, color: string) {
    if ((target.userData as ILineUserData).withDot) {
      const points = Line3d.getPoints(target);
      if (!points) return;
      Line3d.setPointsGeometry(points.geometry, target.userData.keyPositions, color);
      points.geometry.setDrawRange(0, target.userData.keyVectors.length);
    }
  }

  static getVertexCount(target: FatLine) {
    return (target.userData.keyPositions ?? []).length / 3;
  }
  static getVertexFromObject3D(target: FatLine): number[] {
    return target.userData.keyPositions ?? [];
  }

  /**
   * 计算在首/尾添加关键点后曲线的插值点集
   * @param target 目标物
   * @param dot 添加的关键点
   * @param pose 添加的位置
   * @returns newPositions,  新增线段的插值点的位置信息
   * @returns updatePositions 曲线需要更新的点的位置信息
   * @returns offset 曲线需要更新的位置
   */
  static addKeyDot(target: FatLine, dot: Vector3, pose: 'start' | 'end') {
    const keyPoints = target.userData.keyVectors;
    const totalPoints: Vector3[] = [...keyPoints];
    if (pose === 'end') {
      totalPoints.push(dot);
    } else if (pose === 'start') {
      totalPoints.unshift(dot);
    }

    const curve = new CatmullRomCurve3(totalPoints);
    curve.curveType = Spline3d.CURVE_TYPE;
    const curveData = curve.getPoints(Spline3d.INTERPOLATION_COUNT * (totalPoints.length - 1));
    let newPoints: Vector3[] = [];
    let updatePoints: Vector3[] = [];
    let offset;
    if (pose === 'end') {
      offset = Spline3d.INTERPOLATION_COUNT * (keyPoints.length - 2);
      newPoints = curveData.slice(-Spline3d.INTERPOLATION_COUNT - 2);
      updatePoints = curveData.slice(
        Spline3d.INTERPOLATION_COUNT * (keyPoints.length - 2),
        Spline3d.INTERPOLATION_COUNT * (keyPoints.length - 1) + 2
      );
    } else {
      offset = 0;
      newPoints = curveData.slice(0, Spline3d.INTERPOLATION_COUNT + 2);
      updatePoints = curveData.slice(Spline3d.INTERPOLATION_COUNT, Spline3d.INTERPOLATION_COUNT * 2 + 2);
      newPoints.reverse();
    }

    const newPositions = Spline3d.convertVectorsToArray(newPoints);
    const updatePositions = Spline3d.convertVectorsToArray(updatePoints);

    return { newPositions, updatePositions, offset };
  }

  static convertVectorsToArray(points: Vector3[]) {
    const position = [];
    for (let i = 0, l = points.length; i < l; i++) {
      const point = points[i];
      position.push(point.x, point.y, point.z);
    }
    return position;
  }
}
